import {
  ArrowLeft,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  Download,
  FileText,
  Target,
  Users,
} from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { toast } from 'sonner'
import { KpiStatCard } from '@/components/training/KpiStatCard'
import { StatusBadge } from '@/components/training/StatusBadge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useTrainingEnrollments } from '@/hooks/use-training-enrollments'
import {
  useExportSkillCheckCsv,
  useExportTrainingCsv,
  useTrainingReport,
} from '@/hooks/use-training-reports'
import { hasFollowUpSheet } from '@/lib/skill-check'
import { useTenantPath } from '@/lib/tenant'

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6']

const runExport = (exportFn: () => Promise<void>) =>
  exportFn().catch((err) =>
    toast.error(err instanceof Error ? err.message : 'エクスポートに失敗しました'),
  )

export default function TrainingReportPage() {
  const { trainingId = '' } = useParams<{ trainingId: string }>()
  const tp = useTenantPath()
  const { data, isLoading } = useTrainingReport(trainingId)
  const { data: enrollments = [] } = useTrainingEnrollments(trainingId)
  const { exportCsv } = useExportTrainingCsv(trainingId)
  const { exportSkillCheckCsv } = useExportSkillCheckCsv(trainingId)

  const report = data as any
  const followUpEnrollments = enrollments.filter((e) => hasFollowUpSheet(e.status))

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      </div>
    )
  }

  if (!report) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">レポートが見つかりません</p>
      </div>
    )
  }

  const { training, kpi } = report

  const pieData = [
    { name: '完了', value: kpi.completionCount },
    { name: '受講中', value: kpi.enrollmentCount - kpi.completionCount },
  ].filter((d) => d.value > 0)

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to={tp('/teacher/trainings')}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-1 h-4 w-4" />
              戻る
            </Button>
          </Link>
          <h1 className="text-2xl font-bold">KPIレポート</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => runExport(exportCsv)}>
            <Download className="mr-1 h-4 w-4" />
            CSVエクスポート
          </Button>
          <Button variant="outline" onClick={() => runExport(exportSkillCheckCsv)}>
            <Download className="mr-1 h-4 w-4" />
            スキルチェック集計CSV
          </Button>
        </div>
      </div>

      <p className="text-muted-foreground">研修: {training?.title}</p>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KpiStatCard label="受講者数" value={kpi.enrollmentCount} icon={Users} />
        <KpiStatCard
          label="完了率"
          value={kpi.completionRate}
          format="percent"
          icon={CheckCircle2}
        />
        <KpiStatCard label="出席率" value={kpi.attendanceRate} format="percent" icon={Target} />
        <KpiStatCard
          label="アンケート回答率"
          value={kpi.surveyResponseRate}
          format="percent"
          icon={ClipboardList}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">ステータス内訳</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">データなし</p>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {pieData.map((_entry, index) => (
                      <Cell key={`cell-${_entry.name}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">追加KPI</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <KpiStatCard
              label="スキルチェック本人評価の提出率"
              value={kpi.followUpSubmissionRate}
              format="percent"
              icon={FileText}
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            スキルチェック（受講者別・{followUpEnrollments.length}名）
          </CardTitle>
        </CardHeader>
        <CardContent>
          {followUpEnrollments.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              スキルチェック中の受講者はいません
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>受講者</TableHead>
                  <TableHead>ステータス</TableHead>
                  <TableHead className="text-right">評価シート</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {followUpEnrollments.map((enrollment) => (
                  <TableRow key={enrollment.id}>
                    <TableCell className="font-medium">{enrollment.userName ?? '-'}</TableCell>
                    <TableCell>
                      <StatusBadge type="enrollment" status={enrollment.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Link to={tp(`/follow-ups/${enrollment.id}`)}>
                        <Button variant="outline" size="sm">
                          <ClipboardCheck className="mr-1 h-3.5 w-3.5" />
                          開く
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
